import { API_BASE_URL } from '../common/constants';

const handleUnauthorized = (): void => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('permissions');
    window.location.href = '/#/login';
};

export interface ApiConfig {
    baseURL?: string;
    headers?: HeadersInit;
    requireAuth?: boolean;
}

export interface RequestOptions extends RequestInit {
    requireAuth?: boolean;
    baseURL?: string;
}

export interface ApiResponse<T = any> {
    data: T;
    message?: string;
    status?: number;
}

export class ApiError extends Error {
    status?: number;
    data?: any;

    constructor(message: string, status?: number, data?: any) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
        this.data = data;
    }
}

const getAuthToken = (): string | null => {
    return localStorage.getItem('token');
};

const getDefaultHeaders = (requireAuth = true, customHeaders: HeadersInit = {}): HeadersInit => {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(customHeaders as Record<string, string>),
    };

    if (requireAuth) {
        const token = getAuthToken();
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
    }

    return headers;
};

const handleResponse = async <T = any>(response: Response): Promise<T> => {
    const contentType = response.headers.get('content-type');
    const hasJsonContent = contentType && contentType.includes('application/json');

    let data: any;
    if (hasJsonContent) {
        data = await response.json();
    } else {
        data = await response.text();
    }

    if (!response.ok) {
        if (response.status === 401) {
            handleUnauthorized();
            throw new ApiError('Session expired. Please login again.', 401, data);
        }

        let errorMessage = 'An error occurred';

        if (typeof data === 'string') {
            errorMessage = data;
        } else if (data?.message) {
            errorMessage =
                typeof data.message === 'string' ? data.message : JSON.stringify(data.message);
        } else if (data?.error) {
            if (typeof data.error === 'string') {
                errorMessage = data.error;
            } else if (data.error?.message) {
                errorMessage =
                    typeof data.error.message === 'string'
                        ? data.error.message
                        : JSON.stringify(data.error.message);
            } else {
                errorMessage = JSON.stringify(data.error);
            }
        } else if (data) {
            errorMessage = JSON.stringify(data);
        }

        throw new ApiError(errorMessage, response.status, data);
    }

    return data;
};

const apiRequest = async <T = any>(endpoint: string, options: RequestOptions = {}): Promise<T> => {
    const {
        requireAuth = true,
        baseURL = API_BASE_URL,
        headers: customHeaders,
        ...fetchOptions
    } = options;

    const url = `${baseURL}${endpoint}`;
    const headers = getDefaultHeaders(requireAuth, customHeaders);

    try {
        const response = await fetch(url, {
            ...fetchOptions,
            headers,
        });

        return await handleResponse<T>(response);
    } catch (error) {
        if (error instanceof ApiError) {
            throw error;
        }
        throw new ApiError(
            error instanceof Error ? error.message : 'Network error occurred',
            undefined,
            error,
        );
    }
};

export const api = {
    get: <T = any>(endpoint: string, options: RequestOptions = {}): Promise<T> => {
        return apiRequest<T>(endpoint, {
            ...options,
            method: 'GET',
        });
    },

    post: <T = any>(endpoint: string, data?: any, options: RequestOptions = {}): Promise<T> => {
        return apiRequest<T>(endpoint, {
            ...options,
            method: 'POST',
            body: JSON.stringify(data),
        });
    },

    put: <T = any>(endpoint: string, data?: any, options: RequestOptions = {}): Promise<T> => {
        return apiRequest<T>(endpoint, {
            ...options,
            method: 'PUT',
            body: JSON.stringify(data),
        });
    },

    patch: <T = any>(endpoint: string, data?: any, options: RequestOptions = {}): Promise<T> => {
        return apiRequest<T>(endpoint, {
            ...options,
            method: 'PATCH',
            body: JSON.stringify(data),
        });
    },

    delete: <T = any>(endpoint: string, options: RequestOptions = {}): Promise<T> => {
        return apiRequest<T>(endpoint, {
            ...options,
            method: 'DELETE',
        });
    },

    upload: async <T = any>(
        endpoint: string,
        file: File | FileList,
        fieldName = 'file',
        additionalData?: Record<string, any>,
        options: RequestOptions = {},
    ): Promise<T> => {
        const formData = new FormData();

        if (file instanceof FileList) {
            Array.from(file).forEach(f => {
                formData.append(fieldName, f);
            });
        } else {
            formData.append(fieldName, file);
        }

        if (additionalData) {
            Object.entries(additionalData).forEach(([key, value]) => {
                formData.append(key, value);
            });
        }

        const { requireAuth = true, baseURL = API_BASE_URL, ...fetchOptions } = options;

        const headers: Record<string, string> = {};
        if (requireAuth) {
            const token = getAuthToken();
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
        }

        const url = `${baseURL}${endpoint}`;

        try {
            const response = await fetch(url, {
                ...fetchOptions,
                method: 'POST',
                body: formData,
                headers,
            });

            return await handleResponse<T>(response);
        } catch (error) {
            if (error instanceof ApiError) {
                throw error;
            }
            throw new ApiError(
                error instanceof Error ? error.message : 'Upload error occurred',
                undefined,
                error,
            );
        }
    },
};

export const baseApi = {
    get: <T = any>(endpoint: string, options?: Omit<RequestOptions, 'baseURL'>): Promise<T> =>
        api.get(endpoint, { ...options, baseURL: API_BASE_URL }),

    post: <T = any>(
        endpoint: string,
        data?: any,
        options?: Omit<RequestOptions, 'baseURL'>,
    ): Promise<T> => api.post(endpoint, data, { ...options, baseURL: API_BASE_URL }),

    put: <T = any>(
        endpoint: string,
        data?: any,
        options?: Omit<RequestOptions, 'baseURL'>,
    ): Promise<T> => api.put(endpoint, data, { ...options, baseURL: API_BASE_URL }),

    patch: <T = any>(
        endpoint: string,
        data?: any,
        options?: Omit<RequestOptions, 'baseURL'>,
    ): Promise<T> => api.patch(endpoint, data, { ...options, baseURL: API_BASE_URL }),

    delete: <T = any>(endpoint: string, options?: Omit<RequestOptions, 'baseURL'>): Promise<T> =>
        api.delete(endpoint, { ...options, baseURL: API_BASE_URL }),

    upload: <T = any>(
        endpoint: string,
        file: File | FileList,
        fieldName?: string,
        additionalData?: Record<string, any>,
        options?: Omit<RequestOptions, 'baseURL'>,
    ): Promise<T> =>
        api.upload(endpoint, file, fieldName, additionalData, {
            ...options,
            baseURL: API_BASE_URL,
        }),
};

export const goApi = {
    get: <T = any>(endpoint: string, options?: Omit<RequestOptions, 'baseURL'>): Promise<T> =>
        api.get(endpoint, { ...options, baseURL: API_BASE_URL }),

    post: <T = any>(
        endpoint: string,
        data?: any,
        options?: Omit<RequestOptions, 'baseURL'>,
    ): Promise<T> => api.post(endpoint, data, { ...options, baseURL: API_BASE_URL }),

    put: <T = any>(
        endpoint: string,
        data?: any,
        options?: Omit<RequestOptions, 'baseURL'>,
    ): Promise<T> => api.put(endpoint, data, { ...options, baseURL: API_BASE_URL }),

    patch: <T = any>(
        endpoint: string,
        data?: any,
        options?: Omit<RequestOptions, 'baseURL'>,
    ): Promise<T> => api.patch(endpoint, data, { ...options, baseURL: API_BASE_URL }),

    delete: <T = any>(endpoint: string, options?: Omit<RequestOptions, 'baseURL'>): Promise<T> =>
        api.delete(endpoint, { ...options, baseURL: API_BASE_URL }),

    upload: <T = any>(
        endpoint: string,
        file: File | FileList,
        fieldName?: string,
        additionalData?: Record<string, any>,
        options?: Omit<RequestOptions, 'baseURL'>,
    ): Promise<T> =>
        api.upload(endpoint, file, fieldName, additionalData, {
            ...options,
            baseURL: API_BASE_URL,
        }),
};

export default api;
